// Currency Configuration
// CAD is the primary currency

export const CURRENCY_CONFIG = {
  DEFAULT_CURRENCY: 'CAD',
  DEFAULT_AMOUNT: 1.99, // 1.99 CAD
  
  // Exchange rates relative to CAD (1 CAD = X)
  // Update these rates based on current market rates
  EXCHANGE_RATES: {
    CAD: 1.0,
    USD: 0.74,  // 1 CAD = 0.74 USD (approximately)
    EUR: 0.68,  // 1 CAD = 0.68 EUR (approximately)
  },

  CURRENCIES: [
    {
      code: 'CAD',
      name: 'Canadian Dollar',
      symbol: '$',
      emoji: '🍁',
      isDefault: true,
    },
    {
      code: 'USD',
      name: 'US Dollar',
      symbol: '$',
      emoji: '💵',
      isDefault: false,
    },
    {
      code: 'EUR',
      name: 'Euro',
      symbol: '€',
      emoji: '💶',
      isDefault: false,
    },
  ],
};

/**
 * Convert amount from CAD to another currency
 * @param {number} amountInCAD - Amount in Canadian Dollars
 * @param {string} targetCurrency - Target currency code (USD, EUR, CAD, etc.)
 * @returns {number} Converted amount rounded to 2 decimals
 */
export const convertCurrency = (amountInCAD, targetCurrency = 'CAD') => {
  const rate = CURRENCY_CONFIG.EXCHANGE_RATES[targetCurrency];
  
  if (!rate) {
    console.warn(`Currency ${targetCurrency} not found, returning amount in CAD`);
    return parseFloat(amountInCAD).toFixed(2);
  }
  
  return parseFloat((amountInCAD * rate).toFixed(2));
};

/**
 * Get currency symbol
 * @param {string} currencyCode - Currency code (USD, CAD, EUR, etc.)
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
  const currency = CURRENCY_CONFIG.CURRENCIES.find(c => c.code === currencyCode);
  return currency ? currency.symbol : '$';
};

/**
 * Get currency display name with emoji
 * @param {string} currencyCode - Currency code
 * @returns {string} Currency name with emoji
 */
export const getCurrencyDisplay = (currencyCode) => {
  const currency = CURRENCY_CONFIG.CURRENCIES.find(c => c.code === currencyCode);
  return currency ? `${currency.emoji} ${currency.name}` : currencyCode;
};

/**
 * Format price for display
 * @param {number} amount - Amount to display
 * @param {string} currencyCode - Currency code
 * @param {boolean} showCurrency - Whether to show currency code
 * @returns {string} Formatted price string
 */
export const formatPrice = (amount, currencyCode = 'CAD', showCurrency = true) => {
  const symbol = getCurrencySymbol(currencyCode);
  const formatted = parseFloat(amount).toFixed(2);
  
  if (showCurrency) {
    return `${symbol}${formatted} ${currencyCode}`;
  }
  
  return `${symbol}${formatted}`;
};

/**
 * Get premium pricing for different currencies
 * @returns {object} Premium pricing for all currencies
 */
export const getPremiumPricing = () => {
  const pricing = {};
  
  CURRENCY_CONFIG.CURRENCIES.forEach(currency => {
    pricing[currency.code] = convertCurrency(CURRENCY_CONFIG.DEFAULT_AMOUNT, currency.code);
  });
  
  return pricing;
};
